import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { inlineLinkClasses } from '@/components/page';
import { Callout } from '@/components/ui/callout';
import { describedBy, FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { isApiError } from '@/lib/api/client';
import { api } from '@/lib/api/instance';
import { queryKeys } from '@/lib/api/keys';
import { safeNextPath, useDocumentTitle } from '@/lib/hooks';
import { AuthLayout } from './auth-layout';
import { SubmitButton, useFocusAfterRender } from './form-submit';

const LoginFormSchema = z.object({
  email: z.string().trim().min(1, 'Enter your email address.').max(320),
  password: z.string().min(1, 'Enter your password.').max(256),
});
type LoginForm = z.infer<typeof LoginFormSchema>;

export function LoginPage() {
  useDocumentTitle('Sign in');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNextPath(params.get('next'));
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(LoginFormSchema),
    defaultValues: { email: '', password: '' },
  });
  const [rootErrorRef, focusRootError] = useFocusAfterRender<HTMLDivElement>();

  const onSubmit = handleSubmit(async (values) => {
    try {
      const session = await api.login(values);
      queryClient.setQueryData(queryKeys.session, session);
      navigate(next ?? '/', { replace: true });
    } catch (error) {
      focusRootError();
      if (isApiError(error, 'INVALID_CREDENTIALS')) {
        setError('root', { message: 'That email and password do not match an account.' });
      } else {
        setError('root', {
          message: error instanceof Error ? error.message : 'Signing in failed.',
        });
      }
    }
  });

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to manage your servers’ plugin settings."
      footer={
        <>
          New to ComfiConf?{' '}
          <Link
            to={next ? `/register?next=${encodeURIComponent(next)}` : '/register'}
            className={inlineLinkClasses}
          >
            Create an account
          </Link>
        </>
      }
    >
      <form
        onSubmit={onSubmit}
        noValidate
        className="space-y-5"
        aria-describedby={errors.root ? 'login-error' : undefined}
      >
        {errors.root ? (
          <Callout tone="danger" role="alert" id="login-error" ref={rootErrorRef} tabIndex={-1}>
            {errors.root.message}
          </Callout>
        ) : null}
        <FormField id="login-email" label="Email" error={errors.email?.message}>
          <Input
            id="login-email"
            type="email"
            autoComplete="email"
            autoFocus
            aria-invalid={errors.email ? true : undefined}
            aria-describedby={describedBy('login-email', { error: !!errors.email })}
            {...register('email')}
          />
        </FormField>
        <FormField id="login-password" label="Password" error={errors.password?.message}>
          <Input
            id="login-password"
            type="password"
            autoComplete="current-password"
            aria-invalid={errors.password ? true : undefined}
            aria-describedby={describedBy('login-password', { error: !!errors.password })}
            {...register('password')}
          />
        </FormField>
        <SubmitButton variant="primary" size="lg" className="w-full" submitting={isSubmitting}>
          Sign in
        </SubmitButton>
      </form>
    </AuthLayout>
  );
}
